/*
 * Mailcheck https://github.com/mailcheck/mailcheck
 * Author
 * Derrick Ko (@derrickko)
 *
 * Released under the MIT License.
 *
 * Imported from upstream v1.1.2 on 04/2019, with :
 * - linting corrections
 * - domains app specific
 * - domainThreshold 2 => 3 to catch gmail.fr => gmail.com suggestion
 */

const Mailcheck = {
  domainThreshold: 3,
  secondLevelThreshold: 2,
  topLevelThreshold: 2,

  // lists generated from 21 000 confirmed accounts on 11/2020
  // This matches ~95% of accounts.
  defaultDomains: [
    'aliceadsl.fr',
    'aol.com',
    'bbox.fr',
    'bluewin.ch',
    'cegetel.net',
    'club-internet.fr',
    'free.fr',
    'gmail.com',
    'icloud.com',
    'imparato.io',
    'laposte.net',
    'mac.com',
    'me.com',
    'msn.com',
    'netcourrier.com',
    'neuf.fr',
    'numericable.fr',
    'numericable.com',
    'orange.fr',
    'sfr.fr',
    'skynet.be',
    'videotron.ca',
    'wanadoo.fr',
    'yandex.com',
    'ymail.com',
    'yopmail.com',
  ],

  // removed "mail" from upstream config, otherwise this wouldn't catch (g)mail.com errors
  defaultSecondLevelDomains: ['yahoo', 'hotmail', 'live', 'outlook', 'gmx'],

  // order matters
  defaultTopLevelDomains: [
    'com',
    'fr',
    'net',
    'ch',
    'be',
    'ca',
    'org',
    'qc.ca',
    'eu',
    'edu',
    'de',
    'it',
  ],

  /* eslint no-param-reassign: ["error", { "props": false }] */
  run(opts) {
    opts.domains = opts.domains || Mailcheck.defaultDomains;
    opts.secondLevelDomains =
      opts.secondLevelDomains || Mailcheck.defaultSecondLevelDomains;
    opts.topLevelDomains =
      opts.topLevelDomains || Mailcheck.defaultTopLevelDomains;
    opts.distanceFunction = opts.distanceFunction || Mailcheck.sift4Distance;

    const defaultCallback = (result) => result;
    const suggestedCallback = opts.suggested || defaultCallback;
    const emptyCallback = opts.empty || defaultCallback;

    const result = Mailcheck.suggest(
      Mailcheck.encodeEmail(opts.email),
      opts.domains,
      opts.secondLevelDomains,
      opts.topLevelDomains,
      opts.distanceFunction
    );

    return result ? suggestedCallback(result) : emptyCallback();
  },

  suggest(
    inputEmail,
    domains,
    secondLevelDomains,
    topLevelDomains,
    distanceFunction
  ) {
    const email = inputEmail.toLowerCase().trim();

    const emailParts = this.splitEmail(email);

    if (secondLevelDomains && topLevelDomains) {
      // If the email is a valid 2nd-level + top-level, do not suggest anything.
      if (
        secondLevelDomains.indexOf(emailParts.secondLevelDomain) !== -1 &&
        topLevelDomains.indexOf(emailParts.topLevelDomain) !== -1
      ) {
        return false;
      }
    }

    let closestDomain = this.findClosestDomain(
      emailParts.domain,
      domains,
      distanceFunction,
      this.domainThreshold
    );

    if (closestDomain) {
      if (closestDomain === emailParts.domain) {
        // The email address exactly matches one of the supplied domains; do not return a suggestion.
        return false;
      }
      // The email address closely matches one of the supplied domains; return a suggestion
      return {
        address: emailParts.address,
        domain: closestDomain,
        full: `${emailParts.address}@${closestDomain}`,
      };
    }

    // The email address does not closely match one of the supplied domains
    const closestSecondLevelDomain = this.findClosestDomain(
      emailParts.secondLevelDomain,
      secondLevelDomains,
      distanceFunction,
      this.secondLevelThreshold
    );
    const closestTopLevelDomain = this.findClosestDomain(
      emailParts.topLevelDomain,
      topLevelDomains,
      distanceFunction,
      this.topLevelThreshold
    );

    if (emailParts.domain) {
      closestDomain = emailParts.domain;
      let rtrn = false;

      if (
        closestSecondLevelDomain &&
        closestSecondLevelDomain !== emailParts.secondLevelDomain
      ) {
        // The email address may have a mispelled second-level domain; return a suggestion
        closestDomain = closestDomain.replace(
          emailParts.secondLevelDomain,
          closestSecondLevelDomain
        );
        rtrn = true;
      }

      if (
        closestTopLevelDomain &&
        closestTopLevelDomain !== emailParts.topLevelDomain &&
        emailParts.secondLevelDomain !== ''
      ) {
        // The email address may have a mispelled top-level domain; return a suggestion
        closestDomain = closestDomain.replace(
          new RegExp(`${emailParts.topLevelDomain}$`),
          closestTopLevelDomain
        );
        rtrn = true;
      }

      if (rtrn) {
        return {
          address: emailParts.address,
          domain: closestDomain,
          full: `${emailParts.address}@${closestDomain}`,
        };
      }
    }

    /* The email address exactly matches one of the supplied domains, does not closely
     * match any domain and does not appear to simply have a mispelled top-level domain,
     * or is an invalid email address; do not return a suggestion.
     */
    return false;
  },

  findClosestDomain(
    domain,
    domains,
    distanceFunction = this.sift4Distance,
    threshold = this.topLevelThreshold
  ) {
    let dist;
    let minDist = Infinity;
    let closestDomain = null;

    if (!domain || !domains) {
      return false;
    }

    for (let i = 0; i < domains.length; i += 1) {
      if (domain === domains[i]) {
        return domain;
      }
      dist = distanceFunction(domain, domains[i]);
      if (dist < minDist) {
        minDist = dist;
        closestDomain = domains[i];
      }
    }

    if (minDist <= threshold && closestDomain !== null) {
      return closestDomain;
    }
    return false;
  },

  sift4Distance(s1, s2, maxOffset = 5) {
    // sift4: https://siderite.blogspot.com/2014/11/super-fast-and-accurate-string-distance.html

    if (!s1 || !s1.length) {
      if (!s2) {
        return 0;
      }
      return s2.length;
    }

    if (!s2 || !s2.length) {
      return s1.length;
    }

    const l1 = s1.length;
    const l2 = s2.length;

    let c1 = 0; // cursor for string 1
    let c2 = 0; // cursor for string 2
    let lcss = 0; // largest common subsequence
    let localCs = 0; // local common substring
    let trans = 0; // number of transpositions ('ab' vs 'ba')
    const offsetArr = []; // offset pair array, for computing the transpositions

    while (c1 < l1 && c2 < l2) {
      if (s1.charAt(c1) === s2.charAt(c2)) {
        localCs += 1;
        let isTrans = false;
        // see if current match is a transposition
        let i = 0;
        while (i < offsetArr.length) {
          const ofs = offsetArr[i];
          if (c1 <= ofs.c1 || c2 <= ofs.c2) {
            // when two matches cross, the one considered a transposition is the one with the largest difference in offsets
            isTrans = Math.abs(c2 - c1) >= Math.abs(ofs.c2 - ofs.c1);
            if (isTrans) {
              trans += 1;
            } else if (!ofs.trans) {
              ofs.trans = true;
              trans += 1;
            }
            break;
          } else if (c1 > ofs.c2 && c2 > ofs.c1) {
            offsetArr.splice(i, 1);
          } else {
            i += 1;
          }
        }
        offsetArr.push({
          c1,
          c2,
          trans: isTrans,
        });
      } else {
        lcss += localCs;
        localCs = 0;
        if (c1 !== c2) {
          c1 = Math.min(c1, c2); // using min allows the computation of transpositions
          c2 = c1;
        }
        // if matching characters are found, remove 1 from both cursors (they get incremented at the end of the loop)
        // so that we can have only one code block handling matches
        for (let j = 0; j < maxOffset && (c1 + j < l1 || c2 + j < l2); j += 1) {
          if (c1 + j < l1 && s1.charAt(c1 + j) === s2.charAt(c2)) {
            c1 += j - 1;
            c2 -= 1;
            break;
          }
          if (c2 + j < l2 && s1.charAt(c1) === s2.charAt(c2 + j)) {
            c1 -= 1;
            c2 += j - 1;
            break;
          }
        }
      }
      c1 += 1;
      c2 += 1;
      // this covers the case where the last match is on the last token in list, so that it can compute transpositions correctly
      if (c1 >= l1 || c2 >= l2) {
        lcss += localCs;
        localCs = 0;
        c1 = Math.min(c1, c2);
        c2 = c1;
      }
    }
    lcss += localCs;
    return Math.round(Math.max(l1, l2) - lcss + trans); // add the cost of transpositions to the final result
  },

  splitEmail(email) {
    const parts = email.split('@');

    if (parts.length < 2) {
      return false;
    }

    for (let i = 0; i < parts.length; i += 1) {
      if (parts[i] === '') {
        return false;
      }
    }

    const domain = parts.pop();
    const domainParts = domain.split('.');
    let sld = '';
    let tld = '';

    if (domainParts.length === 0) {
      // The address does not have a top-level domain
      return false;
    }
    if (domainParts.length === 1) {
      // The address has only a top-level domain (valid under RFC)
      [tld] = domainParts;
    } else {
      // The address has a domain and a top-level domain
      [sld] = domainParts;
      for (let j = 1; j < domainParts.length; j += 1) {
        tld += `${domainParts[j]}.`;
      }
      tld = tld.substring(0, tld.length - 1);
    }

    return {
      topLevelDomain: tld,
      secondLevelDomain: sld,
      domain,
      address: parts.join('@'),
    };
  },

  // Encode the email address to prevent XSS but leave in valid
  // characters, following this official spec:
  // http://en.wikipedia.org/wiki/Email_address#Syntax
  encodeEmail(email) {
    let result = encodeURI(email);
    result = result
      .replace('%20', ' ')
      .replace('%25', '%')
      .replace('%5E', '^')
      .replace('%60', '`')
      .replace('%7B', '{')
      .replace('%7C', '|')
      .replace('%7D', '}');
    return result;
  },
};

export default Mailcheck;
